import { Component, OnDestroy, OnInit } from '@angular/core';
import { AppState } from '../../store';
import { Store } from '@ngrx/store';
import * as fromPayments from '../../store/payments/payments.reducer';
import { ApiState } from '../../model/api-state.model';
import { combineLatest, first, map, Subject, takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { PaymentsPolicyResult } from '../../model/payments.model';
import * as fromRemote from '../../store/remote/remote.reducer';
import * as fromProcess from '../../store/process/process.reducer';
import { FormsService } from '../../services/forms.service';
import { ProcessService } from '../../services/process.service';
import { ConfirmPayload } from '../../model/confirm.model';
import { ActivatedRoute, Router } from '@angular/router';
import { PersistStorageKeys, PersistStorageService } from '../../services/persist-storage.service';

@Component({
  selector: 'app-page-payment',
  templateUrl: './payment.component.html',
  styleUrls: ['./payment.component.scss'],
})
export class PaymentComponent implements OnInit, OnDestroy {
  ids$ = this.activatedRoute.params.pipe(map(({ id }) => (id ? String(id) : null)));
  paymentsPolicyState$ = this.store.select(fromPayments.selectPaymentsPolicyState);
  paymentsPolicyData$ = this.store.select(fromPayments.selectPaymentsPolicyData);
  paymentsPolicyError$ = this.store.select(fromPayments.selectPaymentsPolicyError);
  processConfirmState$ = this.store.select(fromProcess.selectProcessConfirmState);
  remoteHashCode$ = this.store.select(fromRemote.selectHashCode);
  processHashCode$ = this.store.select(fromProcess.selectProcessHashCode);
  hashCode$ = combineLatest([this.processHashCode$, this.remoteHashCode$]).pipe(
    map(([processHashCode, remoteHashCode]) => {
      if (this.persistStorage.getBoolean(PersistStorageKeys.IsRemote)) {
        return remoteHashCode || undefined;
      }

      return processHashCode || undefined;
    }),
  );
  ngDestroy$ = new Subject<void>();

  apiState = ApiState;

  constructor(
    private store: Store<AppState>,
    private formsService: FormsService,
    private processService: ProcessService,
    private translateService: TranslateService,
    private persistStorage: PersistStorageService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.store.dispatch(fromProcess.actions.setStep({ step: 6 }));
    this.processService.onPageInit();
    this.store.dispatch(fromPayments.actions.resetOnInit());

    this.implementsConfirmListening();
    this.implementsPaymentListening();
  }

  ngOnDestroy(): void {
    this.ngDestroy$.next();
    this.ngDestroy$.complete();
  }

  fetchPolicy() {
    this.hashCode$.pipe(first()).subscribe((hashCode) => {
      this.store.dispatch(fromPayments.actions.fetchPolicyStart({ hashCode }));
    });
  }

  fetchConfirm() {
    this.hashCode$.pipe(first()).subscribe((hashCode) => {
      if (!hashCode) {
        void this.router.navigate(['error']);

        throw new Error('hashCode is not defined');
      }

      const payload: ConfirmPayload = {
        hashCode,
        ...this.processService.apiPayload,
      };

      this.store.dispatch(fromProcess.actions.confirmStart({ payload }));
    });
  }

  private implementsConfirmListening(): void {
    this.processConfirmState$.pipe(takeUntil(this.ngDestroy$)).subscribe((state) => {
      switch (state) {
        case ApiState.success:
          this.fetchPolicy();
          break;
        case ApiState.error:
          break;
        default:
          this.fetchConfirm();
      }
    });
  }

  private implementsPaymentListening(): void {
    combineLatest([this.paymentsPolicyState$, this.paymentsPolicyData$])
      .pipe(takeUntil(this.ngDestroy$))
      .subscribe(([state, data]) => {
        if (state === ApiState.success && data) {
          this.makePayment(data.result);
        }
      });
  }

  private makePayment(result: PaymentsPolicyResult) {
    window.location.href = result.transactionLink;
  }
}
